import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

const EMPTY_PAGE_STATE = { fields: {}, markers: [], overlay_data_url: '' };

const normalizeTemplateState = (template, stateValue) => {
  const pages = {};
  (template?.pages || []).forEach((page) => {
    const incoming = stateValue?.pages?.[page.id] || {};
    pages[page.id] = {
      fields: { ...(incoming.fields || {}) },
      markers: Array.isArray(incoming.markers) ? incoming.markers.map((marker) => ({ ...marker })) : [],
      overlay_data_url: incoming.overlay_data_url || '',
    };
  });
  return { version: 1, pages };
};

const markerStyles = {
  pin: 'bg-rose-500 text-white',
  arrow: 'bg-amber-400 text-slate-900',
  note: 'bg-cyan-500 text-white',
};

const markerGlyph = {
  pin: '•',
  arrow: '➜',
  note: '✎',
};

const TEXT_INK_COLOR = '#1e3a8a';

export const ClinicProcedureTemplateCanvas = ({
  template,
  value,
  onChange,
  stateValue,
  onStateChange,
  testId,
  clearTestId,
  readOnly = false,
}) => {
  const baseCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const interactionRef = useRef(null);
  const composeTimeoutRef = useRef(null);
  const drawingRef = useRef(false);
  const draggingMarkerRef = useRef(null);

  const [activePageId, setActivePageId] = useState(template?.pages?.[0]?.id || '');
  const [tool, setTool] = useState('draw');
  const [markerLabelDraft, setMarkerLabelDraft] = useState('');
  const [markerNoteDraft, setMarkerNoteDraft] = useState('');
  const [selectedMarkerId, setSelectedMarkerId] = useState('');
  const [hasPacket, setHasPacket] = useState(Boolean(value));
  const [pageAspect, setPageAspect] = useState('210 / 297');
  const [localState, setLocalState] = useState(() => normalizeTemplateState(template, stateValue));
  const localStateRef = useRef(localState);

  const activePage = useMemo(
    () => template?.pages?.find((page) => page.id === activePageId) || template?.pages?.[0],
    [activePageId, template],
  );

  const activePageState = useMemo(
    () => localState?.pages?.[activePage?.id] || EMPTY_PAGE_STATE,
    [activePage?.id, localState],
  );

  const selectedMarker = useMemo(
    () => (activePageState.markers || []).find((marker) => marker.id === selectedMarkerId) || null,
    [activePageState.markers, selectedMarkerId],
  );

  useEffect(() => {
    setLocalState(normalizeTemplateState(template, stateValue));
  }, [stateValue, template]);

  useEffect(() => {
    localStateRef.current = localState;
  }, [localState]);

  useEffect(() => {
    setHasPacket(Boolean(value));
  }, [value]);

  const drawMarkersOnContext = useCallback((context, markers, width, height) => {
    (markers || []).forEach((marker) => {
      const x = (marker.x || 0) * width;
      const y = (marker.y || 0) * height;
      context.save();
      if (marker.type === 'text') {
        context.fillStyle = TEXT_INK_COLOR;
        context.font = "600 15px 'Segoe UI', Arial, sans-serif";
        context.textAlign = 'left';
        context.textBaseline = 'middle';
        context.fillText(marker.label || '', x, y);
        context.restore();
        return;
      }
      context.fillStyle = marker.type === 'arrow' ? '#f59e0b' : marker.type === 'note' ? '#06b6d4' : '#ef4444';
      context.strokeStyle = '#0f172a';
      context.lineWidth = 2;
      context.beginPath();
      context.arc(x, y, 14, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      context.fillStyle = '#ffffff';
      context.font = 'bold 14px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(markerGlyph[marker.type] || '•', x, y + 1);
      if (marker.label) {
        context.fillStyle = 'rgba(255,255,255,0.92)';
        context.fillRect(x + 18, y - 14, Math.min(240, (marker.label.length * 8) + 18), 28);
        context.strokeStyle = '#cbd5e1';
        context.strokeRect(x + 18, y - 14, Math.min(240, (marker.label.length * 8) + 18), 28);
        context.fillStyle = '#0f172a';
        context.font = '12px Arial';
        context.textAlign = 'left';
        context.fillText(marker.label, x + 26, y + 1);
      }
      context.restore();
    });
  }, []);

  const drawFieldSummary = useCallback((context, page, pageState, width, height) => {
    const entries = Object.entries(pageState.fields || {}).filter(([, fieldValue]) => fieldValue);
    if (!entries.length) return;
    const summaryRows = entries.slice(0, 6);
    const boxHeight = 26 + (summaryRows.length * 18);
    context.save();
    context.fillStyle = 'rgba(255,255,255,0.92)';
    context.fillRect(22, height - boxHeight - 22, width - 44, boxHeight);
    context.strokeStyle = '#cbd5e1';
    context.strokeRect(22, height - boxHeight - 22, width - 44, boxHeight);
    context.fillStyle = '#0f172a';
    context.font = 'bold 12px Arial';
    context.fillText(page.label, 32, height - boxHeight + 0);
    context.font = '12px Arial';
    summaryRows.forEach(([fieldId, fieldValue], index) => {
      const fieldMeta = (page.fields || []).find((field) => field.id === fieldId);
      context.fillText(`${fieldMeta?.label || fieldId}: ${String(fieldValue).slice(0, 84)}`, 32, height - boxHeight + 20 + (index * 16));
    });
    context.restore();
  }, []);

  const composePacket = useCallback(async (nextState) => {
    if (!template?.pages?.length) {
      onChange('');
      setHasPacket(false);
      return;
    }
    const pageImages = await Promise.all(template.pages.map((page) => loadImage(page.image_url)));
    const pageWidth = Math.max(...pageImages.map((image) => image.naturalWidth));
    const pageGap = 28;
    const totalHeight = pageImages.reduce((sum, image) => sum + image.naturalHeight, 0) + (pageGap * (pageImages.length - 1));
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = pageWidth;
    canvas.height = totalHeight;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, pageWidth, totalHeight);
    let currentY = 0;
    for (let index = 0; index < template.pages.length; index += 1) {
      const page = template.pages[index];
      const pageImage = pageImages[index];
      const pageState = nextState.pages?.[page.id] || EMPTY_PAGE_STATE;
      context.drawImage(pageImage, 0, currentY, pageImage.naturalWidth, pageImage.naturalHeight);
      if (pageState.overlay_data_url) {
        const overlayImage = await loadImage(pageState.overlay_data_url);
        context.drawImage(overlayImage, 0, currentY, pageImage.naturalWidth, pageImage.naturalHeight);
      }
      context.save();
      context.translate(0, currentY);
      drawMarkersOnContext(context, pageState.markers || [], pageImage.naturalWidth, pageImage.naturalHeight);
      drawFieldSummary(context, page, pageState, pageImage.naturalWidth, pageImage.naturalHeight);
      context.restore();
      currentY += pageImage.naturalHeight + pageGap;
    }
    onChange(canvas.toDataURL('image/webp', 0.92));
    const pagesWithContent = Object.values(nextState.pages || {}).filter((pageState) => (
      Object.keys(pageState.fields || {}).length || (pageState.markers || []).length || pageState.overlay_data_url
    ));
    setHasPacket(Boolean(pagesWithContent.length));
  }, [drawFieldSummary, drawMarkersOnContext, onChange, template]);

  const scheduleCompose = useCallback((nextState) => {
    if (composeTimeoutRef.current) window.clearTimeout(composeTimeoutRef.current);
    composeTimeoutRef.current = window.setTimeout(() => {
      composePacket(nextState).catch(() => undefined);
    }, 260);
  }, [composePacket]);

  const commitState = useCallback((nextState, shouldCompose = true) => {
    localStateRef.current = nextState;
    setLocalState(nextState);
    onStateChange?.(nextState);
    if (shouldCompose) scheduleCompose(nextState);
  }, [onStateChange, scheduleCompose]);

  const updateActivePageState = useCallback((updater, shouldCompose = true) => {
    const currentState = localStateRef.current;
    const nextState = {
      ...currentState,
      pages: {
        ...currentState.pages,
        [activePage.id]: updater(currentState.pages?.[activePage.id] || EMPTY_PAGE_STATE),
      },
    };
    commitState(nextState, shouldCompose);
  }, [activePage?.id, commitState]);

  const paintSeqRef = useRef(0);
  const lastPaintedPageRef = useRef('');
  const selfOverlayRef = useRef('');

  const paintActivePage = useCallback(async () => {
    if (!activePage || !baseCanvasRef.current || !overlayCanvasRef.current) return;
    const seq = paintSeqRef.current + 1;
    paintSeqRef.current = seq;
    const pageImage = await loadImage(activePage.image_url);
    if (paintSeqRef.current !== seq || !baseCanvasRef.current || !overlayCanvasRef.current) return;
    setPageAspect(`${pageImage.naturalWidth} / ${pageImage.naturalHeight}`);
    const baseCanvas = baseCanvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    const baseContext = baseCanvas.getContext('2d');
    const overlayContext = overlayCanvas.getContext('2d');
    baseCanvas.width = pageImage.naturalWidth;
    baseCanvas.height = pageImage.naturalHeight;
    overlayCanvas.width = pageImage.naturalWidth;
    overlayCanvas.height = pageImage.naturalHeight;
    baseContext.clearRect(0, 0, baseCanvas.width, baseCanvas.height);
    baseContext.drawImage(pageImage, 0, 0, baseCanvas.width, baseCanvas.height);
    overlayContext.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    overlayContext.lineCap = 'round';
    overlayContext.lineJoin = 'round';
    overlayContext.strokeStyle = '#0f172a';
    overlayContext.lineWidth = 5;
    if (activePageState.overlay_data_url) {
      const overlayImage = await loadImage(activePageState.overlay_data_url);
      if (paintSeqRef.current !== seq) return;
      overlayContext.drawImage(overlayImage, 0, 0, overlayCanvas.width, overlayCanvas.height);
    }
  }, [activePage, activePageState.overlay_data_url]);

  useEffect(() => {
    const currentPageId = activePage?.id || '';
    const pageChanged = lastPaintedPageRef.current !== currentPageId;
    const overlayIsOwnStroke = !pageChanged && !!activePageState.overlay_data_url && activePageState.overlay_data_url === selfOverlayRef.current;
    if (overlayIsOwnStroke) return;
    lastPaintedPageRef.current = currentPageId;
    if (pageChanged) selfOverlayRef.current = '';
    paintActivePage().catch(() => undefined);
  }, [paintActivePage, activePage?.id, activePageState.overlay_data_url]);

  useEffect(() => () => {
    if (composeTimeoutRef.current) window.clearTimeout(composeTimeoutRef.current);
  }, []);

  const getRelativePoint = useCallback((event) => {
    const rect = interactionRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)),
    };
  }, []);

  const getCanvasPoint = useCallback((event) => {
    const canvas = overlayCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const finishStroke = useCallback(async () => {
    if (!drawingRef.current || !overlayCanvasRef.current || !activePage) return;
    drawingRef.current = false;
    const overlayDataUrl = overlayCanvasRef.current.toDataURL('image/png');
    selfOverlayRef.current = overlayDataUrl;
    updateActivePageState((pageState) => ({ ...pageState, overlay_data_url: overlayDataUrl }));
  }, [activePage, updateActivePageState]);

  const handleCanvasPointerDown = (event) => {
    if (readOnly || tool !== 'draw') return;
    event.preventDefault();
    const canvas = overlayCanvasRef.current;
    const context = canvas.getContext('2d');
    const point = getCanvasPoint(event);
    drawingRef.current = true;
    canvas.setPointerCapture(event.pointerId);
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const handleCanvasPointerMove = (event) => {
    if (!drawingRef.current || tool !== 'draw') return;
    const context = overlayCanvasRef.current.getContext('2d');
    const point = getCanvasPoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const handleMarkerLayerClick = (event) => {
    if (readOnly || tool === 'draw' || draggingMarkerRef.current || !activePage) return;
    const point = getRelativePoint(event);
    const nextMarker = {
      id: `marker-${Date.now()}`,
      type: tool,
      x: point.x,
      y: point.y,
      label: markerLabelDraft.trim(),
      note: markerNoteDraft.trim(),
    };
    updateActivePageState((pageState) => ({
      ...pageState,
      markers: [...(pageState.markers || []), nextMarker],
    }));
    setSelectedMarkerId(nextMarker.id);
    if (tool === 'text') setMarkerLabelDraft('');
  };

  const startMarkerDrag = (event, markerId) => {
    if (readOnly) return;
    event.stopPropagation();
    draggingMarkerRef.current = markerId;
    setSelectedMarkerId(markerId);
  };

  const handleInteractionPointerMove = (event) => {
    if (!draggingMarkerRef.current || !activePage) return;
    const point = getRelativePoint(event);
    const nextState = {
      ...localStateRef.current,
      pages: {
        ...localStateRef.current.pages,
        [activePage.id]: {
          ...(localStateRef.current.pages?.[activePage.id] || EMPTY_PAGE_STATE),
          markers: (localStateRef.current.pages?.[activePage.id]?.markers || []).map((marker) => (
            marker.id === draggingMarkerRef.current ? { ...marker, x: point.x, y: point.y } : marker
          )),
        },
      },
    };
    localStateRef.current = nextState;
    setLocalState(nextState);
  };

  const handleInteractionPointerUp = () => {
    if (!draggingMarkerRef.current || !activePage) return;
    draggingMarkerRef.current = null;
    commitState(localStateRef.current);
  };

  const updateMarker = (markerId, updates) => {
    updateActivePageState((pageState) => ({
      ...pageState,
      markers: (pageState.markers || []).map((marker) => (marker.id === markerId ? { ...marker, ...updates } : marker)),
    }));
  };

  const deleteSelectedMarker = () => {
    if (!selectedMarkerId) return;
    updateActivePageState((pageState) => ({
      ...pageState,
      markers: (pageState.markers || []).filter((marker) => marker.id !== selectedMarkerId),
    }));
    setSelectedMarkerId('');
  };

  const clearCurrentPage = async () => {
    if (!activePage) return;
    selfOverlayRef.current = '';
    const nextState = {
      ...localStateRef.current,
      pages: {
        ...localStateRef.current.pages,
        [activePage.id]: {
          ...EMPTY_PAGE_STATE,
        },
      },
    };
    setSelectedMarkerId('');
    commitState(nextState);
    await paintActivePage();
  };

  const clearAllPages = async () => {
    selfOverlayRef.current = '';
    const nextState = normalizeTemplateState(template, null);
    setSelectedMarkerId('');
    commitState(nextState);
    onChange('');
    setHasPacket(false);
    await paintActivePage();
  };

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-4" data-testid={`${testId}-wrapper`}>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900" data-testid={`${testId}-label`}>{template?.name || 'Plantilla maestra clínica'}</p>
          <p className="mt-1 text-xs text-slate-500" data-testid={`${testId}-helper`}>Todas las páginas del PDF son editables: escribe texto sobre cualquier renglón, dibuja, añade pines, flechas y notas. Se guarda todo y se reabre exactamente como quedó.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${hasPacket ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`${testId}-status`}>
            {hasPacket ? 'interactivo guardado' : 'pendiente'}
          </span>
          {!!template?.pdf_url && <a href={template.pdf_url} target="_blank" rel="noreferrer" className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600" data-testid={`${testId}-open-pdf-button`}>Abrir PDF original</a>}
          {!readOnly && <button type="button" onClick={clearCurrentPage} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600" data-testid={clearTestId}>Limpiar página</button>}
          {!readOnly && <button type="button" onClick={clearAllPages} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600" data-testid={`${testId}-clear-all-button`}>Limpiar todo</button>}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid={`${testId}-page-selector`}>
        {(template?.pages || []).map((page, index) => (
          <button
            key={page.id}
            type="button"
            onClick={() => setActivePageId(page.id)}
            className={`rounded-full px-4 py-2 text-xs font-semibold ${activePageId === page.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
            data-testid={`${testId}-page-${index + 1}`}
          >
            {index + 1}. {page.label}
          </button>
        ))}
      </div>

      {!!activePage && (
        <div className="mt-4 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`${testId}-page-details`}>
            <p className="text-sm font-semibold text-slate-900">{activePage.label}</p>
            <p className="mt-1 text-xs text-slate-500">{activePage.description}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(activePage.highlights || []).map((item) => (
                <span key={item} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#075985]">{item}</span>
              ))}
            </div>
          </div>

          {!readOnly && (
            <div className="rounded-[22px] border border-slate-200 bg-white p-4" data-testid={`${testId}-tools-panel`}>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Herramientas</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(template?.tools || []).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTool(item.id)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold ${tool === item.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                    data-testid={`${testId}-tool-${item.id}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {tool !== 'draw' && (
                <div className="mt-4 space-y-3" data-testid={`${testId}-marker-controls`}>
                  <input
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    placeholder={tool === 'text' ? 'Escribe el texto y toca el renglón del documento' : 'Título del punto / flecha'}
                    value={selectedMarker ? selectedMarker.label : markerLabelDraft}
                    onChange={(event) => {
                      if (selectedMarker) updateMarker(selectedMarker.id, { label: event.target.value });
                      else setMarkerLabelDraft(event.target.value);
                    }}
                    data-testid={`${testId}-marker-label-input`}
                  />
                  {tool !== 'text' && <textarea
                    className="min-h-[88px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                    placeholder="Nota o detalle en ese punto"
                    value={selectedMarker ? selectedMarker.note : markerNoteDraft}
                    onChange={(event) => {
                      if (selectedMarker) updateMarker(selectedMarker.id, { note: event.target.value });
                      else setMarkerNoteDraft(event.target.value);
                    }}
                    data-testid={`${testId}-marker-note-input`}
                  />}
                  {!!selectedMarker && (
                    <button type="button" onClick={deleteSelectedMarker} className="rounded-full bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600" data-testid={`${testId}-marker-delete-button`}>
                      Eliminar marcador seleccionado
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 rounded-[24px] border border-slate-200 bg-slate-50 p-3">
        <div
          ref={interactionRef}
          className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white"
          style={{ aspectRatio: pageAspect }}
          onPointerMove={handleInteractionPointerMove}
          onPointerUp={handleInteractionPointerUp}
          onPointerLeave={handleInteractionPointerUp}
          data-testid={testId}
        >
          <canvas ref={baseCanvasRef} className="absolute inset-0 h-full w-full" />
          <canvas
            ref={overlayCanvasRef}
            className={`absolute inset-0 h-full w-full ${tool === 'draw' && !readOnly ? 'touch-none' : 'pointer-events-none'}`}
            onPointerDown={handleCanvasPointerDown}
            onPointerMove={handleCanvasPointerMove}
            onPointerUp={finishStroke}
            onPointerLeave={finishStroke}
            onPointerCancel={finishStroke}
            data-testid={`${testId}-overlay`}
          />
          <div
            className={`absolute inset-0 ${tool === 'draw' || readOnly ? 'pointer-events-none' : 'pointer-events-auto'}`}
            onClick={handleMarkerLayerClick}
            data-testid={`${testId}-marker-layer`}
          >
            {(activePageState.markers || []).map((marker) => (
              marker.type === 'text' ? (
                <button
                  key={marker.id}
                  type="button"
                  onPointerDown={(event) => startMarkerDrag(event, marker.id)}
                  onClick={(event) => { event.stopPropagation(); setSelectedMarkerId(marker.id); }}
                  className={`absolute -translate-y-1/2 whitespace-nowrap rounded px-1 text-[13px] font-semibold ${selectedMarkerId === marker.id ? 'bg-blue-100/80 ring-2 ring-blue-300' : 'bg-transparent'}`}
                  style={{ left: `${(marker.x || 0) * 100}%`, top: `${(marker.y || 0) * 100}%`, color: TEXT_INK_COLOR, pointerEvents: readOnly ? 'none' : 'auto' }}
                  data-testid={`${testId}-marker-${marker.id}`}
                >
                  {marker.label || 'Escribe el texto en el panel ➜'}
                </button>
              ) : (
                <button
                  key={marker.id}
                  type="button"
                  onPointerDown={(event) => startMarkerDrag(event, marker.id)}
                  onClick={(event) => { event.stopPropagation(); setSelectedMarkerId(marker.id); }}
                  className={`absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-sm font-bold shadow-lg ${markerStyles[marker.type] || markerStyles.pin} ${selectedMarkerId === marker.id ? 'ring-4 ring-slate-900/10' : ''}`}
                  style={{ left: `${(marker.x || 0) * 100}%`, top: `${(marker.y || 0) * 100}%`, pointerEvents: readOnly ? 'none' : 'auto' }}
                  data-testid={`${testId}-marker-${marker.id}`}
                >
                  {markerGlyph[marker.type] || '•'}
                </button>
              )
            ))}
          </div>
        </div>
      </div>

      {!!selectedMarker && (
        <div className="mt-4 rounded-[22px] border border-slate-200 bg-white p-4" data-testid={`${testId}-selected-marker-card`}>
          <p className="text-sm font-semibold text-slate-900">Marcador activo</p>
          <p className="mt-1 text-xs text-slate-500">{selectedMarker.label || 'Sin título'} · {selectedMarker.note || 'Sin nota'}</p>
        </div>
      )}

      {!!activePage?.fields?.length && (
        <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid={`${testId}-fields-grid`}>
          {activePage.fields.map((field) => {
            const fieldValue = activePageState.fields?.[field.id] || '';
            const commonProps = {
              value: fieldValue,
              disabled: readOnly,
              placeholder: field.placeholder,
              onChange: (event) => updateActivePageState((pageState) => ({
                ...pageState,
                fields: { ...(pageState.fields || {}), [field.id]: event.target.value },
              })),
              className: 'w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm',
              'data-testid': `${testId}-field-${field.id}`,
            };
            return (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-400">{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea {...commonProps} className="min-h-[96px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
                ) : (
                  <input {...commonProps} type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {!!value && (
        <div className="mt-4 overflow-hidden rounded-[24px] border border-slate-200 bg-white" data-testid={`${testId}-saved-preview`}>
          <img src={value} alt="Plantilla clínica guardada" className="w-full object-contain" />
        </div>
      )}
    </div>
  );
};
